import { Box, Flex } from '@withjoy/joykit';
import React, { useRef } from 'react';
import { Filters } from './Filters';
import { ActiveFiltersRow, unpresentedActiveFilters } from './components/ActiveFiltersRow';
import { ResetAllFiltersButton } from './components/ResetAllFiltersButton';
import type { FilterFacet } from './Filter.types';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useDesignGalleryFiltersContext } from '../../DesignGalleryFiltersProvider';
import { SearchBox } from '../Search/SearchBox';
import { useSearchBoxWidth } from './useSearchBoxWidth';

type SearchAndFiltersContainerProps = Readonly<{
  filterFacets: FilterFacet[];
  trailingAction?: React.ReactNode;
  /** Which facet's rail is open, the toggle a pill click runs, and the rail's `id`. */
  facetSidebar: Readonly<{ activeGroupCode: string | null; onToggle: (groupCode: string) => void; railId: string }>;
}>;

export const SearchAndFiltersContainer = (props: SearchAndFiltersContainerProps) => {
  const { filterFacets, trailingAction, facetSidebar } = props;
  const isMobileOrTablet = useIsMobileOrTablet();
  const { activePrintFilters } = useDesignGalleryFiltersContext();
  // Jeff (2026-10-13) for desktop, Arun (2026-10-30) for mobile: the desktop pills and the mobile drawer's section headings
  // already name the selection, so neither surface chips anything they cover. The exception is a
  // filter no facet presents — the nav's "Printed" link sets `format:paper`, and `format` is not
  // a presented group — which would otherwise filter the grid with no control anywhere on the page.
  const unpresentedCodes = unpresentedActiveFilters(filterFacets, activePrintFilters);
  const showActiveChips = unpresentedCodes.length > 0;

  const [previewWithPhotoEnabled] = useIsFeatureEnabled('printPreviewWithPhotoCards');

  const pillHeight = previewWithPhotoEnabled ? 48 : 40;
  const resetAction = <ResetAllFiltersButton pillHeight={pillHeight} />;

  const filterRowRef = useRef<HTMLDivElement>(null);
  const pillsRef = useRef<HTMLDivElement>(null);
  const searchGroupRef = useRef<HTMLDivElement>(null);
  const searchBoxWidth = useSearchBoxWidth(filterRowRef, pillsRef, searchGroupRef, activePrintFilters);

  // New filter-bar design (Figma) is gated behind the preview-with-photo flag: filters on the left,
  // search + preview button right-aligned, all at a consistent 40px height.
  if (previewWithPhotoEnabled) {
    return (
      <>
        {/* Wrap the two rows in one element so PageSection's column rowGap spaces the whole bar
            from the gallery, not the primary row from its overflow row. */}
        <Box>
          <Flex
            ref={filterRowRef}
            alignItems={{ _: 'center', sm2: 'flex-start' }}
            gap={{ _: 3, sm2: 0 }}
            paddingLeft={{ _: 6, sm2: 6 }}
            paddingRight={{ _: 4, sm2: 6 }}
            flexWrap={{ _: 'wrap', sm2: 'nowrap' }}
          >
            {/* On mobile the filter controls sit to the RIGHT of the search bar (order 2);
                on desktop they lead the row (order 0). `useSearchBoxWidth` narrows the search
                box when that lets the pills fit on one line. */}
            <Flex
              ref={pillsRef}
              order={{ _: 2, sm2: 0 }}
              flex={{ _: 'none', sm2: 1 }}
              paddingY="1px"
              alignItems="flex-start"
              gap={2}
              flexWrap="wrap"
              minWidth={0}
            >
              <Filters
                facets={filterFacets}
                pillHeight={pillHeight}
                facetSidebar={facetSidebar}
                trailing={resetAction}
              />
            </Flex>
            <Flex
              ref={searchGroupRef}
              order={{ _: 1, sm2: 0 }}
              flex={{ _: 1, sm2: 'none' }}
              alignItems="center"
              gap={3}
              paddingLeft={{ _: 0, sm2: 3 }}
              minWidth={0}
            >
              <SearchBox
                compact
                desktopWidth={searchBoxWidth}
              />
              {!isMobileOrTablet && trailingAction}
            </Flex>
          </Flex>
          {showActiveChips && (
            <Box
              paddingLeft={{ _: 6, sm2: 6 }}
              paddingRight={{ _: 4, sm2: 6 }}
              paddingTop={3}
            >
              <ActiveFiltersRow compositeCodes={unpresentedCodes} />
            </Box>
          )}
        </Box>
        {isMobileOrTablet && trailingAction && (
          <Flex
            position="fixed"
            bottom={6}
            zIndex="dropdown"
            justifyContent="center"
            width="100%"
            pointerEvents="none"
          >
            <Box pointerEvents="auto">{trailingAction}</Box>
          </Flex>
        )}
      </>
    );
  }

  // Original layout (flag off).
  return (
    <>
      <Box>
        <Flex
          paddingLeft={{ _: 6, sm2: 8 }}
          paddingRight={{ _: 4, sm2: 6 }}
          paddingY="1px"
          alignItems="center"
          gap={{ _: 16, sm2: 24 }}
          flexWrap="wrap"
        >
          <SearchBox />
          <Filters
            facets={filterFacets}
            facetSidebar={facetSidebar}
            trailing={resetAction}
          />
        </Flex>
        {showActiveChips && (
          <Box
            paddingLeft={{ _: 6, sm2: 8 }}
            paddingRight={{ _: 4, sm2: 6 }}
            paddingTop={3}
          >
            <ActiveFiltersRow compositeCodes={unpresentedCodes} />
          </Box>
        )}
      </Box>
    </>
  );
};
